// Inputs for a list of `FieldSpec` settings, rendered from what the backend
// declares so a new data source or signal data type needs no UI work: a data
// source's per-step limits (Clay's spend cap, Notion's pages) and a signal
// data type's trigger settings (which Notion pages to watch).

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Plus, Trash2 } from "lucide-mui";
import type { FieldOption, FieldOptions, FieldSpec, PropertyCondition } from "../../api/client.ts";
import {
  NO_VALUE_OPS,
  conditionsOf,
  isConditionIncomplete,
  isFieldMissing,
  isFieldOutOfRange,
  rangeText,
} from "./fieldSpecs.ts";

const NO_OPTIONS: FieldOption[] = [];

interface FieldProps {
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
}

const labelOf = (field: FieldSpec, v: string) => field.option_labels?.[v] ?? v;

// A multi-select of `options` (string_list) or of the tenant's choices (choice_list).
function ListInput({ field, value, onChange, disabled, choices }: FieldProps & { choices: FieldOption[] }) {
  const selected = Array.isArray(value) ? (value as string[]) : [];
  const isChoice = field.type === "choice_list";
  const missing = field.required && isFieldMissing(field, value);
  // Saved values the choices no longer hold stay listed, so they can be dropped.
  const unknown = selected.filter((v) => !choices.some((c) => c.value === v));
  const all = [...choices, ...unknown.map((v) => ({ value: v, label: isChoice ? `Unknown (${v})` : v }))];
  const label = (v: string) => all.find((c) => c.value === v)?.label ?? v;
  const helper =
    isChoice && choices.length === 0
      ? [field.help_text, "Nothing to choose from yet."].filter(Boolean).join(" ")
      : field.help_text || undefined;
  return (
    <TextField
      label={field.label}
      select
      size="small"
      value={selected}
      onChange={(e) => {
        const picked = e.target.value as unknown as string[];
        // Option order, so re-picking the same set never reads as a change.
        onChange(all.map((c) => c.value).filter((v) => picked.includes(v)));
      }}
      slotProps={{
        select: {
          multiple: true,
          // An empty choice_list means every choice.
          displayEmpty: isChoice && !field.required,
          renderValue: (v) => {
            const values = v as string[];
            if (!values.length) return isChoice && !field.required ? "All" : "";
            return values.map(label).join(", ");
          },
        },
        inputLabel: isChoice && !field.required ? { shrink: true } : undefined,
      }}
      error={missing}
      helperText={missing ? "Required" : helper}
      disabled={disabled}
      fullWidth
    >
      {all.map((c) => (
        <MenuItem key={c.value} value={c.value}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function NumberInput({ field, value, onChange, disabled }: FieldProps) {
  const number = typeof value === "number" ? value : typeof value === "string" ? value : "";
  const money = field.type === "number";
  const missing = field.required && isFieldMissing(field, value);
  const outOfRange = isFieldOutOfRange(field, value);
  return (
    <TextField
      label={field.label}
      type="number"
      size="small"
      value={number}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw === "") return onChange(null);
        const n = Number(raw);
        onChange(field.type === "integer" ? Math.round(n) : n);
      }}
      slotProps={{
        htmlInput: {
          min: field.minimum ?? undefined,
          max: field.maximum ?? undefined,
          step: money ? "any" : 1,
        },
        input: money ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined,
      }}
      error={missing || outOfRange}
      helperText={
        missing ? "Required" : outOfRange ? `Must be ${rangeText(field)}.` : field.help_text || undefined
      }
      disabled={disabled}
      fullWidth
    />
  );
}

function ConditionsInput({ field, value, onChange, disabled }: FieldProps) {
  const rows = conditionsOf(value);
  const ops = field.options ?? [];
  const set = (next: PropertyCondition[]) => onChange(next);
  const patch = (i: number, p: Partial<PropertyCondition>) =>
    set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const missing = field.required && rows.length === 0;
  return (
    <Stack spacing={1}>
      <Box>
        <Typography variant="body2">{field.label}</Typography>
        {field.help_text && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            {field.help_text}
          </Typography>
        )}
        {missing && (
          <Typography variant="caption" sx={{ color: "error.main", display: "block" }}>
            Add at least one condition.
          </Typography>
        )}
      </Box>
      {rows.map((r, i) => {
        const incomplete = isConditionIncomplete(r);
        const needsValue = !NO_VALUE_OPS.has(r.op);
        return (
          <Box
            key={i}
            sx={{ p: 1, bgcolor: "action.hover", borderRadius: 1, display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              <TextField
                label="Property"
                size="small"
                value={r.property}
                onChange={(e) => patch(i, { property: e.target.value })}
                error={incomplete && !r.property.trim()}
                disabled={disabled}
                fullWidth
              />
              {!disabled && (
                <IconButton size="small" onClick={() => set(rows.filter((_, j) => j !== i))} aria-label="Remove condition">
                  <Trash2 fontSize="small" />
                </IconButton>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Condition"
                select
                size="small"
                value={r.op}
                onChange={(e) =>
                  patch(i, { op: e.target.value, ...(NO_VALUE_OPS.has(e.target.value) ? { value: "" } : {}) })
                }
                error={!r.op}
                disabled={disabled}
                sx={{ minWidth: 130, flexShrink: 0 }}
              >
                {ops.map((o) => (
                  <MenuItem key={o} value={o}>
                    {labelOf(field, o)}
                  </MenuItem>
                ))}
                {r.op && !ops.includes(r.op) && <MenuItem value={r.op}>{r.op}</MenuItem>}
              </TextField>
              {needsValue && (
                <TextField
                  label="Value"
                  size="small"
                  value={r.value}
                  onChange={(e) => patch(i, { value: e.target.value })}
                  error={incomplete && !!r.op && !r.value.trim()}
                  disabled={disabled}
                  fullWidth
                />
              )}
            </Stack>
          </Box>
        );
      })}
      {!disabled && (
        <Button
          size="small"
          startIcon={<Plus />}
          onClick={() => set([...rows, { property: "", op: ops[0] ?? "", value: "" }])}
          sx={{ alignSelf: "flex-start" }}
        >
          Add condition
        </Button>
      )}
    </Stack>
  );
}

function FieldInput(props: FieldProps & { choices: FieldOption[] }) {
  const { field, value, onChange, disabled } = props;
  switch (field.type) {
    case "string_list":
      return (
        <ListInput
          {...props}
          choices={(field.options ?? []).map((o) => ({ value: o, label: labelOf(field, o) }))}
        />
      );
    case "choice_list":
      return <ListInput {...props} />;
    case "conditions":
      return <ConditionsInput {...props} />;
    case "boolean":
      return (
        <Box>
          <FormControlLabel
            control={
              <Switch checked={value === true} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
            }
            label={field.label}
          />
          {field.help_text && (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {field.help_text}
            </Typography>
          )}
        </Box>
      );
    case "string":
    case "text": {
      const missing = field.required && isFieldMissing(field, value);
      return (
        <TextField
          label={field.label}
          size="small"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          error={missing}
          helperText={missing ? "Required" : field.help_text || undefined}
          disabled={disabled}
          multiline={field.type === "text"}
          minRows={field.type === "text" ? 3 : undefined}
          fullWidth
        />
      );
    }
    case "choice": {
      const missing = field.required && isFieldMissing(field, value);
      const current = typeof value === "string" ? value : "";
      const options = field.options ?? [];
      return (
        <TextField
          label={field.label}
          select
          size="small"
          value={current}
          onChange={(e) => onChange(e.target.value || null)}
          error={missing}
          helperText={missing ? "Required" : field.help_text || undefined}
          disabled={disabled}
          fullWidth
        >
          {!field.required && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {options.map((o) => (
            <MenuItem key={o} value={o}>
              {labelOf(field, o)}
            </MenuItem>
          ))}
          {/* A stored value the options no longer hold stays visible, to be changed. */}
          {current && !options.includes(current) && <MenuItem value={current}>{current}</MenuItem>}
        </TextField>
      );
    }
    default:
      return <NumberInput {...props} />;
  }
}

export default function FieldSpecInputs({
  fields,
  fieldOptions,
  values,
  onChange,
  disabled,
}: {
  fields: FieldSpec[];
  // Per-tenant choices for `choice_list` fields, by field name.
  fieldOptions?: FieldOptions | null;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  disabled: boolean;
}) {
  if (!fields.length) return null;
  return (
    <Stack spacing={1.5}>
      {fields.map((f) => (
        <FieldInput
          key={f.name}
          field={f}
          value={values[f.name]}
          choices={fieldOptions?.[f.name] ?? NO_OPTIONS}
          onChange={(value) => onChange({ ...values, [f.name]: value })}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
}
